import React, { FC, PropsWithChildren, useState } from 'react';
import { Collapse, Dropdown as ReactBootstrapDropdown } from 'react-bootstrap';
import { Folder2Open, Icon, ThreeDots } from 'react-bootstrap-icons';
import classNames from 'classnames';

import { createUseThemedStyles } from '@/hooks';
import { Dropdown, DropdownMenuV2, DropdownToggleV2 } from '@/components';
import { focus } from '@/jss/mixins';
import { ThemeConfig } from '@/contexts';

const innerButton = (theme: ThemeConfig) => ({
	border: 0,
	padding: 5,
	flexShrink: 0,
	display: 'flex',
	borderRadius: 2,
	appearance: 'none',
	alignItems: 'center',
	color: theme.colors.primary,
	backgroundColor: 'transparent',
	'&:hover': {
		backgroundColor: theme.colors.gray400,
	},
	'&:active': {
		backgroundColor: theme.colors.gray500,
	},
	'&:disabled': {
		backgroundColor: 'transparent',
	},
	'&:focus-visible': {
		...focus(theme.colors.primary),
	},
});

const useStyles = createUseThemedStyles((theme) => ({
	aiNavigationButton: {
		display: 'flex',
		borderRadius: 5,
		alignItems: 'center',
		justifyContent: 'space-between',
		'& .collapse-button-outer': {
			flexShrink: 0,
			padding: 5,
		},
		'& .count-outer': {
			flexShrink: 0,
			padding: '5px 10px 5px 0',
			'& .count': {
				height: 16,
				minWidth: 16,
				padding: '0 6px',
				borderRadius: 500,
				fontSize: '1.0rem',
				textAlign: 'center',
				lineHeight: '1.6rem',
				color: theme.colors.darkBlue,
				backgroundColor: theme.colors.gray300,
			},
		},
		'& .dropdown-outer': {
			flexShrink: 0,
			display: 'none',
			padding: 5,
		},
		'&:hover, &:focus-within': {
			backgroundColor: theme.colors.gray300,
			'& .dropdown-outer': {
				display: 'block',
			},
		},
		'&.active': {
			backgroundColor: theme.colors.gray300,
		},
	},
	collapseButton: {
		...innerButton(theme),
	},
	button: {
		flex: 1,
		border: 0,
		padding: 10,
		display: 'flex',
		textAlign: 'left',
		overflow: 'hidden',
		appearance: 'none',
		alignItems: 'center',
		borderRadius: 'inherit',
		backgroundColor: 'transparent',
		...theme.fonts.xs,
		...theme.fonts.openSansRegular,
		'& span': {
			overflow: 'hidden',
			whiteSpace: 'nowrap',
			textOverflow: 'ellipsis',
		},
		'&:hover': {
			backgroundColor: 'transparent',
		},
		'&:active': {
			backgroundColor: theme.colors.gray400,
		},
		'&:disabled': {
			backgroundColor: 'transparent',
		},
		'&.indent': {
			paddingLeft: 10 + 16 + 10,
		},
		'&:focus-visible': {
			...focus(theme.colors.primary),
		},
	},
	dropdownToggle: {
		...innerButton(theme),
	},
}));

type AiNavigationButtonVariant = 'primary' | 'secondary';

interface AiNavigationButtonProps {
	id: string;
	title: string;
	icon?: Icon;
	variant?: AiNavigationButtonVariant;
	className?: string;
	onClick?(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void;
	menuItems?: JSX.Element;
	disabled?: boolean;
	active?: boolean;
	indent?: boolean;
	count?: number;
}

export const AiNavigationButton: FC<PropsWithChildren<AiNavigationButtonProps>> = ({
	id,
	title,
	icon,
	variant = 'secondary',
	className,
	onClick,
	menuItems,
	disabled,
	active,
	indent,
	count,
	children,
}) => {
	const classes = useStyles();
	const [show, setShow] = useState(false);

	const ButtonIcon = icon;
	const hasChildren = React.Children.count(children) > 0;

	return (
		<>
			<div
				className={classNames(classes.aiNavigationButton, className, {
					active,
				})}
			>
				{hasChildren && (
					<div className="collapse-button-outer">
						<button
							className={classes.collapseButton}
							onClick={() => {
								setShow((v) => !v);
							}}
						>
							{ButtonIcon && <>{show ? <Folder2Open size={16} /> : <ButtonIcon size={16} />}</>}
						</button>
					</div>
				)}
				<button
					title={title}
					className={classNames(classes.button, {
						indent: indent,
						'pl-0': hasChildren,
					})}
					onClick={onClick}
					disabled={disabled}
				>
					{!hasChildren && ButtonIcon && (
						<ButtonIcon
							className="text-primary mr-2 flex-shrink-0"
							size={16}
							onClick={() => {
								setShow((v) => !v);
							}}
						/>
					)}
					<span
						className={classNames({
							'font-weight-bold': variant === 'primary',
							'text-primary': variant === 'primary',
							'text-primary-dark': variant === 'secondary',
						})}
					>
						{title}
					</span>
				</button>
				{typeof count === 'number' && (
					<div className="count-outer">
						<div className="count">{count}</div>
					</div>
				)}
				{menuItems && (
					<div className="dropdown-outer">
						<Dropdown>
							<ReactBootstrapDropdown.Toggle
								id={id}
								as={DropdownToggleV2}
								className={classes.dropdownToggle}
								disabled={disabled}
							>
								<ThreeDots size={16} />
							</ReactBootstrapDropdown.Toggle>
							<DropdownMenuV2 popperConfig={{ strategy: 'fixed' }} renderOnMount>
								{menuItems}
							</DropdownMenuV2>
						</Dropdown>
					</div>
				)}
			</div>
			{hasChildren && (
				<Collapse in={show}>
					<div>{children}</div>
				</Collapse>
			)}
		</>
	);
};
